import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { getUserTimezone, updateUserTimezone } from '../lib/supabase'
import { Clock, X, Check, CreditCard } from 'lucide-react'
import { BillingManagement } from './BillingManagement'
import { NotificationContactSettings } from './NotificationContactSettings'
import { Button } from './ui/button'
import { Skeleton } from './ui/skeleton'
import { AUTOMATIC_TIMEZONE, getDeviceTimezone, getTimezoneOptions } from '../lib/userTimezone'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select'

interface UserSettingsProps {
  isOpen: boolean
  onClose: () => void
}

export const UserSettings: React.FC<UserSettingsProps> = ({ isOpen, onClose }) => {
  const { user, userTeams } = useAuth()
  const [timezone, setTimezone] = useState(AUTOMATIC_TIMEZONE)
  const [deviceTimezone, setDeviceTimezone] = useState(getDeviceTimezone)
  const [settingsLoaded, setSettingsLoaded] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [activeTab, setActiveTab] = useState<'general' | 'subscription'>('general')

  useEffect(() => {
    if (!isOpen || !user?.id) return
    let cancelled = false
    setLoading(true)
    setSettingsLoaded(false)
    setError('')
    setSuccess('')
    const refreshDeviceTimezone = () => setDeviceTimezone(getDeviceTimezone())
    refreshDeviceTimezone()
    window.addEventListener('focus', refreshDeviceTimezone)
    document.addEventListener('visibilitychange', refreshDeviceTimezone)
    getUserTimezone(user.id).then(savedTimezone => {
      if (!cancelled) {
        setTimezone(savedTimezone ?? AUTOMATIC_TIMEZONE)
        setSettingsLoaded(true)
      }
    }).catch(err => {
      if (!cancelled) setError(err.message || 'Failed to load settings')
    }).finally(() => { if (!cancelled) setLoading(false) })
    return () => {
      cancelled = true
      window.removeEventListener('focus', refreshDeviceTimezone)
      document.removeEventListener('visibilitychange', refreshDeviceTimezone)
    }
  }, [isOpen, user?.id, reloadKey])

  const timezones = useMemo(() => getTimezoneOptions(deviceTimezone, timezone === AUTOMATIC_TIMEZONE ? null : timezone), [deviceTimezone, timezone])
  const effectiveTimezone = timezone === AUTOMATIC_TIMEZONE ? deviceTimezone : timezone

  const handleSave = async () => {
    if (!user || !settingsLoaded || loading || saving) return

    setSaving(true)
    setError('')
    setSuccess('')

    try {
      await updateUserTimezone(user.id, timezone === AUTOMATIC_TIMEZONE ? null : timezone)
      setSuccess('Settings saved successfully!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err: any) {
      console.error('Error saving settings:', err)
      setError(err.message || 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const getCurrentTime = () => {
    try {
      return new Date().toLocaleString('en-US', {
        timeZone: effectiveTimezone,
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      })
    } catch (error) {
      return 'Invalid timezone'
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="w-full max-w-4xl max-h-[90vh] m-4 overflow-hidden border border-border bg-background shadow-lg">
        <div className="flex items-center justify-between border-b border-border p-6">
          <h2 className="text-lg font-semibold text-foreground">Settings</h2>
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
            className="size-11 text-muted-foreground hover:text-foreground"
          >
            <X className="!size-6" />
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6">
            <Button
              onClick={() => setActiveTab('general')}
              variant="ghost"
              className={`h-auto rounded-none py-4 px-1 border-b-2 font-medium text-sm hover:bg-transparent ${
                activeTab === 'general'
                  ? 'border-primary text-primary hover:text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4" />
                <span>General</span>
              </div>
            </Button>
            {/* ROLE AUDIT (2026-09-29): Subscription tab stays admin-only, matching
                BillingManagement's own explicit decision -- see its header comment. */}
            {userTeams && userTeams.length > 0 && (userTeams[0].role === 'admin' || userTeams[0].role === 'owner') && (
              <Button
                onClick={() => setActiveTab('subscription')}
                variant="ghost"
                className={`h-auto rounded-none py-4 px-1 border-b-2 font-medium text-sm hover:bg-transparent ${
                  activeTab === 'subscription'
                    ? 'border-primary text-primary hover:text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <CreditCard className="h-4 w-4" />
                  <span>Subscription</span>
                </div>
              </Button>
            )}
          </nav>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="space-y-6">
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <Skeleton className="h-5 w-5 rounded-full" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <Skeleton className="h-10 w-full" />
                <Skeleton className="mt-2 h-4 w-64" />
              </div>
              <div className="flex justify-end space-x-3">
                <Skeleton className="h-10 w-20" />
                <Skeleton className="h-10 w-32" />
              </div>
            </div>
          ) : (
            <>
              {/* General Tab */}
              {activeTab === 'general' && (
                <div className="space-y-6">
                  {/* Timezone Setting */}
                  <div>
                    <div className="flex items-center space-x-2 mb-3">
                      <Clock className="h-5 w-5 text-muted-foreground" />
                      <label htmlFor="timezone" className="text-sm font-medium text-foreground">
                        Timezone
                      </label>
                    </div>
                    <Select value={timezone} onValueChange={value => { setTimezone(value); setSuccess('') }} disabled={saving || !settingsLoaded}>
                      <SelectTrigger id="timezone" className="w-full">
                        <SelectValue placeholder="Select a timezone" />
                      </SelectTrigger>
                      <SelectContent className="max-w-[calc(100vw-2rem)]">
                        <SelectItem value={AUTOMATIC_TIMEZONE}>Automatic (use device timezone)</SelectItem>
                        {timezones.map((tz) => (
                          <SelectItem key={tz.value} value={tz.value}>
                            {tz.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm text-muted-foreground">Device timezone: {deviceTimezone}</p>
                      {timezone !== AUTOMATIC_TIMEZONE && <Button type="button" variant="ghost" size="sm"
                        disabled={saving || !settingsLoaded}
                        onClick={() => { setTimezone(AUTOMATIC_TIMEZONE); setSuccess('') }}>Use device timezone</Button>}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">Automatic follows this device. Manual choices stay fixed. Save Settings to apply changes. This does not change your team's reminder schedule.</p>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Current time: {getCurrentTime()}
                    </p>
                  </div>

                  {userTeams && userTeams.length > 0 && (userTeams[0].role === 'owner' || userTeams[0].role === 'admin' || userTeams[0].role === 'manager') && (
                    <NotificationContactSettings teamId={userTeams[0].team_id} userRole={userTeams[0].role} />
                  )}

                  {/* Error/Success Messages */}
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <div role="alert" className="text-sm text-red-700">{error}</div>
                      {!settingsLoaded && <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => setReloadKey(value => value + 1)}>Retry loading settings</Button>}
                    </div>
                  )}

                  {success && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-3">
                      <div className="flex items-center text-sm text-green-700">
                        <Check className="h-4 w-4 mr-2" />
                        {success}
                      </div>
                    </div>
                  )}

                  {/* Save Button */}
                  <div className="flex justify-end space-x-3">
                    <Button
                      onClick={onClose}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={saving || loading || !settingsLoaded}
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <span>Save Settings</span>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Subscription Tab */}
              {activeTab === 'subscription' && userTeams && userTeams.length > 0 && (
                <BillingManagement 
                  teamId={userTeams[0].team_id} 
                  userRole={userTeams[0].role}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
